import React, { useEffect, useMemo, useState, useContext, useCallback } from "react";
import { useForm } from "react-hook-form";
import { Box, Stack, Step, StepLabel, Stepper } from "@mui/material";
import Divider from "components/Divider/Divider";
import InfoSection from "./Component/InfoSection";
import ConfigSection from "./Component/ConfigSection";
import ProcessorSection from "./Component/ProcessorSection";
import AgentAssignmentSection from "./Component/AgentAssignmentSection";
import { Button } from "@mui/material";
import { useTranslation } from "react-i18next";
import {
  queryDropdownOptions,
  queryMerchantDetail,
} from "../offline-merchant-detail/model";
import { addMdrMerchant, createMerchant, onboardProcessors } from "./model";
import useSnackbarMAS from "utils/useSnackbarMAS/useSnackbarMAS";
import merchantManagement from "routes/merchantManagement";
import useColors from "utils/useColors/useColors";
import KycStatusAndDocumentUploadSection from "./Component/KycStatusAndDocumentUploadSection";
import { formPreventEnterSubmit } from "utils/utils";
import { PortalContext } from "App";
import ButtonMAS from "ButtonMAS/ButtonMAS";
import { useAgentOptionsCache } from "utils/optionsCache/agentOptionsCache";
import { useMerchantOptionsCache } from "utils/optionsCache/merchantOptionsCache";

/** @param {import("./Component/ProcessorTable").processorFormValue} processorFormValue */
const getMdrListFromForm = (processorFormValue) => {
  /** @param {string} s */
  const handleNumString = (s) =>
    Math.round(((Number(s) || 0) / 100) * 1e10) / 1e10; // prevent floating point error

  return Object.entries(processorFormValue).map(
    ([cardTypeKey, cardTypeValue]) => {
      let gateways = Object.entries(cardTypeValue.gateways).map(
        ([gatewayKey, gatewayValue]) => ({
          cardType: cardTypeKey,
          ...(gatewayValue.businessPlan && {
            businessPlan: gatewayValue.businessPlan,
          }),
          paymentGateway: gatewayKey,
          cost: 0,
          ...(gatewayValue.mcc && { mcc: gatewayValue.mcc }),
          enabled: gatewayValue.enabled,
          inUse: gatewayKey === cardTypeValue.inUse,
        }),
      );

      if (["OCTOPUS", "AMEX"].includes(cardTypeKey)) {
        // Special handling for OCTOPUS and AMEX, where only one gateway can be enabled
        gateways = gateways.filter(({ enabled }) => enabled);
      }

      return {
        cardType: cardTypeKey,
        mdr: handleNumString(cardTypeValue.domesticMdr),
        foreignMdr: handleNumString(cardTypeValue.foreignMdr),
        referrerCommission: handleNumString(cardTypeValue.referrerCommission),
        gatewaySwitchingInfoList: gateways,
      };
    },
  );
};

/** @param {import("./Component/ProcessorTable").processorFormValue} processorFormValue */
const getPreferenceFromForm = (processorFormValue) => {
  return JSON.stringify({
    gatewayMap: {
      MASKED: "NONE",
      JCB: processorFormValue?.JCB?.inUse || "NONE",
      VERVE: "NONE",
      ALIPAY: processorFormValue?.ALIPAY?.inUse || "NONE",
      AMEX: processorFormValue?.AMEX?.inUse || "NONE",
      LOYALTY: "NONE",
      DISCOVER: "NONE",
      MASTER: processorFormValue?.MASTER?.inUse || "NONE",
      DINERS: "NONE",
      TNG: "NONE",
      VISA: processorFormValue?.VISA?.inUse || "NONE",
      UNIONPAY: processorFormValue?.UNIONPAY?.inUse || "NONE",
      WECHAT: processorFormValue?.WECHAT?.inUse || "NONE",
      OCTOPUS: processorFormValue?.OCTOPUS?.inUse || "NONE",
      PAYME: processorFormValue?.PAYME?.inUse || "NONE",
    },
    businessRules: null,
    partialAuthIndicator: null,
    networkOrder: null,
  });
};

/** @param {import("./Component/ProcessorTable").processorFormValue} processorFormValue */
const getOnboardProcessorsFromForm = (processorFormValue) => {
  // Obtain array of enabled gateways wihtout duplicates
  const enabledGateways = [
    ...new Set(
      Object.values(processorFormValue)
        .flatMap(({ gateways }) => {
          return Object.entries(gateways).map(([gatewayKey, gatewayValue]) => {
            return gatewayValue.enabled ? gatewayKey : null;
          });
        })
        .filter(Boolean),
    ).values(),
  ];

  // Full list:  ["ALLINPAY", "FUBON", "YEDPAY", "AMEX_BBMSL", "AMEX", "OCTOPUS", "OCTOPUS_BBMSL", "OCTOPUS_HKBN", "WECHAT_PAY", "ALIPAY", "HSBC"]
  const ONBOARD_ORDER = ["ALLINPAY", "FUBON", "YEDPAY", "AMEX_BBMSL", "WECHAT_PAY", "ALIPAY", "HSBC"];
  return ONBOARD_ORDER.filter((gateway) => enabledGateways.includes(gateway));
};

const PAGE_LOCALE_KEY = "page.merchants.";

// Alipay config (Deprecated for pfaas, will now be handled by backend)
// const PARTNER_ID = "2088031571574577";
// const PARTNER_KEY = "yteulb5zxa47h99k8imkoplecxcey738";

const AddOfflineMerchant = ({ path, redirectFn }) => {
  const c = useColors();
  const { t } = useTranslation();
  const { snackbarSuccess, snackbarError } = useSnackbarMAS();
  const { fetchAgentOptions } = useAgentOptionsCache();
  const { invalidateMerchantOptionsCache } = useMerchantOptionsCache();
  const formProps = useForm({
    mode: "onChange",
  });
  const { handleSubmit, trigger, setValue } = formProps;
  const { detailPageState } = useContext(PortalContext);
  const pageState = detailPageState[path];

  useEffect(() => {
    if (pageState && pageState.isoAgentEmail) {
      setValue("isoAgentEmail", pageState.isoAgentEmail);
    }
  }, [pageState, setValue]);

  const steps = useMemo(
    () => [
      {
        key: "agentAssignment",
        label: t("page.merchants.merchantDetail.agentAssignment.title", {
          defaultValue: "Assign Agent",
        }),
      },
      {
        key: "info",
        label: t("page.merchants.merchantDetail.info.title", {
          defaultValue: "Info",
        }),
      },
      {
        key: "config",
        label: t("page.merchants.merchantDetail.config.title", {
          defaultValue: "Config",
        }),
      },
      {
        key: "processor",
        label: t("page.merchants.merchantDetail.processorMdr.title", {
          defaultValue: "Payment Gateway Switching & MDR",
        }),
      },
      {
        key: "kycStatusAndDocumentUpload",
        label: t("page.merchants.merchantDetail.kycStatusAndDocumentUpload.title", {
          defaultValue: "Document Upload",
        }),
      },
    ],
    [t],
  );

  const [activeStep, setActiveStep] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [dropdownOptions, setDropdownOptions] = useState(undefined);
  const [createdMerchantId, setCreatedMerchantId] = useState(null);
  const [createdMerchant, setCreatedMerchant] = useState(null);
  const [localAgentList, setLocalAgentList] = useState([]);
  const [hasMoreAgents, setHasMoreAgents] = useState(false);

  useEffect(() => {
    const fetchOptions = async () => {
      try {
        const resp = await queryDropdownOptions();
        const data = resp?.data?.data;
        console.log("data", data);
        setDropdownOptions({
          serviceIntegratorList: data?.serviceIntegratorList,
          referrerList: data?.referrerList,
          salesList: data?.salesList,
          mccList: (data?.mccList ?? []).map((mccObj) => ({
            key: mccObj.key,
            title: mccObj.title,
            value: mccObj.key,
          })),
        });
      } catch (e) {
        console.error("Failed to fetch dropdown options", e);
      }
    };
    fetchOptions();
  }, []);

  useEffect(() => {
    const fetchAllAgents = async () => {
      try {
        const { options, hasMoreOptions } = await fetchAgentOptions();
        setLocalAgentList(options);
        setHasMoreAgents(hasMoreOptions);
      } catch (error) {
        console.error("Failed to fetch agent list", error);
      }
    };
    fetchAllAgents();
  }, [fetchAgentOptions]);

  const defaultAgent = React.useMemo(() => {
    return pageState?.isoAgentEmail ? {
      key: pageState.isoAgentEmail,
      title: pageState.isoAgentId ? `${pageState.isoAgentId} - ${pageState.firstName || ""} ${pageState.lastName || ""} - ${pageState.isoAgentEmail}` : pageState.isoAgentEmail,
      value: pageState.isoAgentEmail,
      id: pageState.isoAgentId,
      email: pageState.isoAgentEmail
    } : null;
  }, [pageState]);

  const handleAgentSearch = useCallback(async (keyword) => {
    if (typeof keyword !== "string") return [];
    const trimmed = keyword.trim();
    if (!trimmed) return [];

    try {
      const { options } = await fetchAgentOptions(trimmed);
      return options;
    } catch (error) {
      console.error("Failed to search agent list", error);
      return [];
    }
  }, [fetchAgentOptions]);

  const next = async () => {
    // Validate current step before moving forward
    const valid = await trigger(undefined, { shouldFocus: true });
    if (!valid) return;
    setActiveStep((s) => Math.min(s + 1, steps.length - 1));
  };

  const finishAndRedirect = () => {
    if (!createdMerchantId) return;
    const merchantDetailPath = "/merchant-management/merchants/offline-merchant-detail";
    const redirectPayload = {
      name:
        merchantManagement
          .find((tab) => tab.tabName === "Merchants")
          .page.find((page) => page.path === merchantDetailPath).name + createdMerchantId,
      path: merchantDetailPath,
      state: {
        merchantId: createdMerchantId,
        redirectFn: redirectFn,
      },
    };
    // Close the add merchant tab first, then redirect to the new merchant detail tab
    redirectFn("close", { tabId: path });
    redirectFn("redirectPage", redirectPayload);
  };

  const submitAndGoToDocumentStep = async () => {
    // Create merchant and MDR before document upload
    const valid = await trigger(undefined, { shouldFocus: true });
    if (!valid) return;
    const result = await onSubmit(formProps.getValues(), {
      redirectAfter: false,
      fetchMerchantDetail: false,
    });
    // Only move to next step if merchant creation succeeded
    if (result?.success) {
      setActiveStep((s) => Math.min(s + 1, steps.length - 1));
      if (result?.merchantId) {
        try {
          const merchantResponse = await queryMerchantDetail(result.merchantId);
          const merchantData = merchantResponse?.data;
          if (merchantData?.responseCode === "0000" && merchantData?.data) {
            setCreatedMerchant(merchantData.data);
          }
        } catch (error) {
          console.error("Failed to fetch merchant details:", error);
        }
      }
    }
  };

  const back = () => setActiveStep((s) => Math.max(0, s - 1));

  const onSubmit = async (
    formValue,
    { redirectAfter = true, fetchMerchantDetail = !redirectAfter } = {},
  ) => {
    const valid = await trigger(undefined, { shouldFocus: true });
    if (!valid) return { success: false };

    try {
      setIsLoading(true);

      const { isKDollar, ...otherFields } = formValue;
      const campaign = [isKDollar && "KDOLLAR"].filter(Boolean);

      const mdrList = getMdrListFromForm(formValue.processors);
      const preference = getPreferenceFromForm(formValue.processors);
      const enabledGateways = getOnboardProcessorsFromForm(formValue.processors);
      delete otherFields.processors;

      const fubonMasterProcessor = formProps.getValues("processors.MASTER.gateways.FUBON") || {};
      const fubonVisaProcessor = formProps.getValues("processors.VISA.gateways.FUBON") || {};

      const createMerchantParam = {
        ...otherFields,
        otherInfo: { campaign },
        mdrList: mdrList,
        preference: preference,

        // Fubon bank fields
        isFubonBank: fubonMasterProcessor.enabled || fubonVisaProcessor.enabled,
        masterBusinessPlan: fubonMasterProcessor.businessPlan,
        visaBusinessPlan: fubonVisaProcessor.businessPlan,

        // ALIPAY fields (Deprecated for pfaas, will now be handled by backend)
        // partnerId: PARTNER_ID,
        // partnerKey: PARTNER_KEY,

        // isGatewaySwitchingAvailable is now always true since new merchants won't be using DSB/DBS/globalPayments
        isGatewaySwitchingAvailable: true,
        status: "PENDING",
      }

      const createMerchantResp = await createMerchant(createMerchantParam);
      console.log("createMerchantResp", createMerchantResp);

      if (createMerchantResp?.data?.responseCode !== "0000") {
        snackbarError({
          message: t(PAGE_LOCALE_KEY + "addMerchant.fetchMessage.addMerchantFailed"),
          durationInMs: 5000
        });
        return { success: false };
      }
      invalidateMerchantOptionsCache();

      const respData = createMerchantResp?.data?.data;
      const addMdrMerchantParam = {
        merchantId: respData.merchantId,
        merchantName: formValue.name,
        bankAccount: formValue.bankAccount,
        beneficiary: formValue.beneficiary,
        settleCycle: formValue.settleCycle,
        email: formValue.email,
        bankAccountRemarks: formValue.bankAccountRemarks,
        referrer: formValue.referrer
          ? {
            id: formValue.referrer,
          }
          : null,
        sales: formValue.sales
          ? {
            id: formValue.sales,
          }
          : null,
        onboardSales: formValue.sales
          ? {
            id: formValue.sales,
          }
          : null,
        zeroPlan: formValue.zeroPlan,

        mdrList: mdrList,
      }

      const addMdrMerchantResp = await addMdrMerchant(addMdrMerchantParam);
      console.log("addMdrMerchantResp", addMdrMerchantResp);
      if (addMdrMerchantResp?.data?.responseCode !== "0000") {
        //TODO: Might need to add handling for merchant entity created but merchant info create fail

        snackbarError({
          message: t(PAGE_LOCALE_KEY + "addMerchant.fetchMessage.addMerchantMdrFailed"),
          durationInMs: 5000
        });
        return { success: false };
      }

      const onboardProcessorsParam = {
        merchantId: respData.merchantId,
        processors: enabledGateways,
      }

      const onboardProcessorsResp = await onboardProcessors(onboardProcessorsParam);
      console.log("onboardProcessorsResp", onboardProcessorsResp);
      if (onboardProcessorsResp?.data?.responseCode !== "0000") {
        // Proceed to merchant detail page even if onboarding external processors failed
        snackbarError({
          message: t(PAGE_LOCALE_KEY + "addMerchant.fetchMessage.onboardProcessorsAccountFailed"),
          durationInMs: 5000
        });
      }

      snackbarSuccess({
        message: t(PAGE_LOCALE_KEY + "addMerchant.fetchMessage.createMerchantSuccess"),
        durationInMs: 5000
      });

      setCreatedMerchantId(respData.merchantId);

      // Fetch merchant details for KYC status update
      if (fetchMerchantDetail && respData.merchantId) {
        try {
          const merchantResponse = await queryMerchantDetail(respData.merchantId);
          const merchantData = merchantResponse?.data;
          if (merchantData?.responseCode === "0000" && merchantData?.data) {
            setCreatedMerchant(merchantData.data);
          }
        } catch (error) {
          console.error("Failed to fetch merchant details:", error);
        }
      }

      if (redirectAfter) {
        const merchantDetailPath = "/merchant-management/merchants/offline-merchant-detail";
        const redirectPayload = {
          name:
            merchantManagement
              .find((tab) => tab.tabName === "Merchants")
              .page.find((page) => page.path === merchantDetailPath).name + respData.merchantId,
          path: merchantDetailPath,
          state: {
            merchantId: respData.merchantId,
            redirectFn: redirectFn,
          },
        };
        redirectFn("redirectPage", redirectPayload);
        redirectFn("close", { tabId: path });
      }

      return { success: true, merchantId: respData.merchantId };
    } catch (error) {
      console.error("Create Merchant error:", error);
      snackbarError({
        message: `${t(PAGE_LOCALE_KEY + "addMerchant.fetchMessage.createMerchantFailed")}: ${error.message}`,
        durationInMs: 5000
      });
      return { success: false };
    } finally {
      setIsLoading(false);
    }
  };

  const effectiveAgentList = useMemo(() => {
    const list = localAgentList;
    if (defaultAgent && !list.some((a) => a.value === defaultAgent.value)) {
      return [defaultAgent, ...list];
    }
    return list;
  }, [localAgentList, defaultAgent]);

  return (
    <Stack
      direction="column"
      sx={{ padding: "24px", background: "white", gap: "24px" }}
    >
      <Stepper
        activeStep={activeStep}
        alternativeLabel
        sx={{
          "& .MuiStepIcon-root.Mui-active": { color: c.brand1 },
          "& .MuiStepIcon-root.Mui-completed": { color: c.brand1 },
        }}
      >
        {steps.map((s) => (
          <Step key={s.key}>
            <StepLabel>{s.label}</StepLabel>
          </Step>
        ))}
      </Stepper>
      <form noValidate onSubmit={handleSubmit(onSubmit)} onKeyDown={formPreventEnterSubmit}>
        <Box sx={{ maxWidth: "1600px" }}>
          {activeStep === 0 && (
            <AgentAssignmentSection
              formProps={formProps}
              isLoading={isLoading}
              agentList={effectiveAgentList}
              onSearch={handleAgentSearch}
              defaultAgent={defaultAgent}
              hasMoreAgents={hasMoreAgents}
            />
          )}
          {activeStep === 1 && (
            <InfoSection
              formProps={formProps}
              isLoading={isLoading}
              mccDropdownOptions={dropdownOptions?.mccList}
              dropdownOptions={dropdownOptions}
              hideAgentFields={true}
            />
          )}

          {activeStep === 2 && (
            <ConfigSection
              formProps={formProps}
              isEdit
              isLoading={isLoading}
            />
          )}

          {activeStep === 3 && (
            <ProcessorSection
              formProps={formProps}
              isLoading={isLoading}
              mccDropdownOptions={dropdownOptions?.mccList}
            />
          )}
          {activeStep === 4 && (
            <Box sx={{ mt: 2 }}>
              <KycStatusAndDocumentUploadSection
                merchant={createdMerchant}
                merchantId={createdMerchantId}
              />
            </Box>
          )}
        </Box>
        <Divider style={{ margin: "24px 0" }} />

        <Stack
          direction="row"
          justifyContent="space-between"
          flexWrap="wrap"
          gap={2}
        >
          <Button
            variant="outline-normal-button"
            disabled={activeStep === 0 || isLoading}
            sx={{ visibility: activeStep === 0 || activeStep === steps.length - 1 ? "hidden" : "visible" }}
            onClick={back}
            type="button"
          >
            {t("general.button.previous", { defaultValue: "Previous" })}
          </Button>
          {activeStep < steps.length - 2 && (
            <Button
              variant="normal-button"
              onClick={next}
              type="button"
              disabled={isLoading}
            >
              {t("general.button.next", { defaultValue: "Next" })}
            </Button>
          )}
          {activeStep === steps.length - 2 && (
            <ButtonMAS
              isLoading={isLoading}
              disabled={isLoading}
              type="button"
              onClick={submitAndGoToDocumentStep}
            >
              {t("general.button.submit", { defaultValue: "Submit" })}
            </ButtonMAS>
          )}
          {activeStep === steps.length - 1 && (
            <Button
              variant="normal-button"
              type="button"
              onClick={finishAndRedirect}
              disabled={!createdMerchantId}
            >
              {t("general.button.finish", { defaultValue: "Finish" })}
            </Button>
          )}
        </Stack>
      </form>
    </Stack>
  );
};

export default React.memo(AddOfflineMerchant);
